import React, { useEffect, useMemo, useState } from "react";
import { Landmark, Tags } from "lucide-react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { ResponsiveDataList } from "../../components/responsive";
import DeleteModel from "../../components/DeleteModal";
import ParishCategoryApi from "../../api/parishCategoryApi";
import DeaneryApi from "../../api/deaneryApi";
import Helpers from "../../components/Helpers";
import { useAuthUser } from "../../hooks/useAuthUser";
import MasterPageShell from "../../components/masterData/MasterPageShell";
import MasterSectionCard from "../../components/masterData/MasterSectionCard";
import MasterListToolbar from "../../components/masterData/MasterListToolbar";
import MasterToolbarButton from "../../components/masterData/MasterToolbarButton";
import MasterRowActions from "../../components/masterData/MasterRowActions";
import MasterDetailBlock from "../../components/masterData/MasterDetailBlock";
import styles from "../../components/masterData/MasterData.module.scss";
import { trimIdentifierFields } from "../../utils/trimFormFields";
import { DeaneryCard } from "./Parishes/manage/cards/DeaneryCard";
import { DeaneryForm } from "./Parishes/manage/DeaneryForm";

const emptyForm = {
  deaneryName: "",
  deaneryCode: "",
  deanVicar: "",
  parishLifeLiaison: "",
  liaisonPhone: "",
  parishCategoryId: "",
};

const sortDeaneries = (rows: any[]) =>
  [...(rows || [])].sort((a, b) =>
    String(a.deaneryName || "").localeCompare(String(b.deaneryName || ""), undefined, {
      sensitivity: "base",
    }),
  );

const Deaneries = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = useAuthUser();
  const parishCategoryApi = useMemo(() => new ParishCategoryApi(), []);
  const deaneryApi = useMemo(() => new DeaneryApi(), []);

  const [data, setData] = useState<any>({ ...emptyForm });
  const [list, setList] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [editingId, setEditingId] = useState<any>("");
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchKey, setSearchKey] = useState(
    () => (location.state as any)?.searchKeyword || "",
  );
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categoryParishes, setCategoryParishes] = useState<any[]>([]);
  const [selectedParishIds, setSelectedParishIds] = useState<number[]>([]);
  const [parishFilter, setParishFilter] = useState("");
  const [saving, setSaving] = useState(false);

  const isAdmin = userData?.userRole === "ADMIN";
  const isEditing = editingId !== "";

  const loadCategories = () => {
    parishCategoryApi.getAllCategories().then((res: any) => {
      if (res?.success) {
        const sorted = [...res.data].sort((a, b) =>
          a.name.localeCompare(b.name),
        );
        setCategories(sorted);
      }
    });
  };

  const loadList = (categoryId = categoryFilter) => {
    setLoading(true);
    deaneryApi
      .list(categoryId || undefined)
      .then((res: any) => {
        setList(sortDeaneries(res?.data || []));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const loadParishes = (categoryId: string, deaneryId?: number) => {
    if (!categoryId) {
      setCategoryParishes([]);
      setSelectedParishIds([]);
      return;
    }
    deaneryApi.parishesForCategory(categoryId).then((res: any) => {
      const rows = [...(res?.data || [])].sort((a, b) =>
        String(a.parish || "").localeCompare(String(b.parish || ""), undefined, {
          sensitivity: "base",
        }),
      );
      setCategoryParishes(rows);
      if (deaneryId) {
        setSelectedParishIds(
          rows
            .filter((row) => Number(row.deaneryId) === Number(deaneryId))
            .map((row) => Number(row.id)),
        );
      } else {
        setSelectedParishIds([]);
      }
    });
  };

  useEffect(() => {
    loadCategories();
    if (searchKey) {
      search(searchKey);
    } else {
      loadList("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearForm = () => {
    setData({ ...emptyForm, parishCategoryId: categoryFilter });
    setEditingId("");
    setValidated(false);
    setParishFilter("");
    if (categoryFilter) {
      loadParishes(categoryFilter);
    } else {
      setCategoryParishes([]);
      setSelectedParishIds([]);
    }
  };

  const handleChanges = (e: any) => {
    const { name, value } = e.target;
    setData((current) => ({ ...current, [name]: value }));
    if (name === "parishCategoryId") {
      loadParishes(value);
    }
  };

  const openCreatedDeanery = (created: any) => {
    setEditingId(created.id);
    setValidated(false);
    setData({
      deaneryName: created.deaneryName || "",
      deaneryCode: created.deaneryCode || "",
      deanVicar: created.deanVicar || "",
      parishLifeLiaison: created.parishLifeLiaison || "",
      liaisonPhone: created.liaisonPhone || "",
      parishCategoryId: created.parishCategoryId
        ? String(created.parishCategoryId)
        : "",
    });
    setParishFilter("");
    loadParishes(String(created.parishCategoryId || ""), created.id);
    document.getElementById("container-scroller")?.scrollIntoView();
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.checkValidity()) {
      setValidated(true);
      return;
    }

    const payload = trimIdentifierFields(data, ["deaneryName", "deaneryCode"]);
    const editing = isEditing;
    setSaving(true);
    const request = editing
      ? deaneryApi.updateDeanery(payload, editingId)
      : deaneryApi.addDeanery(payload);

    request
      .then(async (res: any) => {
        if (!res?.success) {
          toast.error(res?.msg || "Something went wrong");
          return;
        }

        const deaneryId = editing ? editingId : res.data?.id;
        if (!deaneryId) {
          toast.error("Deanery was saved, but its id was missing.");
          return;
        }
        const assigned = await deaneryApi.assignParishes(
          deaneryId,
          selectedParishIds,
        );
        if (!assigned?.success) {
          toast.error(assigned?.msg || "Unable to save parish assignments.");
          if (!editing && res.data?.id) openCreatedDeanery(res.data);
          return;
        }
        toast.success(editing ? "Deanery updated." : "Deanery created.");
        clearForm();

        if (searchKey) search(searchKey);
        else loadList();
      })
      .catch(() => toast.error("Something went wrong"))
      .finally(() => setSaving(false));
  };

  const startEdit = (row: any) => {
    setEditingId(row.id);
    setValidated(false);
    setData({
      deaneryName: row.deaneryName || "",
      deaneryCode: row.deaneryCode || "",
      deanVicar: row.deanVicar || "",
      parishLifeLiaison: row.parishLifeLiaison || "",
      liaisonPhone: row.liaisonPhone || "",
      parishCategoryId: row.parishCategoryId ? String(row.parishCategoryId) : "",
    });
    setParishFilter("");
    loadParishes(String(row.parishCategoryId || ""), row.id);
    document.getElementById("container-scroller")?.scrollIntoView();
  };

  const deleteDeanery = () => {
    if (!deleteTarget) return;
    deaneryApi.deleteDeanery(deleteTarget.id).then((res: any) => {
      if (res?.success) {
        toast.success(res.msg);
        if (editingId === deleteTarget.id) clearForm();
        if (searchKey) search(searchKey);
        else loadList();
      } else {
        toast.error(res?.msg || "Something went wrong");
      }
      setDeleteTarget(null);
    });
  };

  const search = (key: string) => {
    setLoading(true);
    if (!key) {
      loadList();
      return;
    }
    deaneryApi
      .searchDeanery(key)
      .then((res: any) => {
        setList(sortDeaneries(res?.data || []));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const onSearch = (event: any) => {
    const key = event.target.value;
    setSearchKey(key);
    search(key);
  };

  const onCategoryFilter = (value: string) => {
    setCategoryFilter(value);
    setSearchKey("");
    if (!isEditing) {
      setData((current) => ({ ...current, parishCategoryId: value }));
      if (value) loadParishes(value);
      else {
        setCategoryParishes([]);
        setSelectedParishIds([]);
      }
    }
    loadList(value);
  };

  const openParishes = (row: any) => {
    navigate("/parish", {
      state: {
        parishCategoryId: row.parishCategoryId ? String(row.parishCategoryId) : "",
        deaneryId: row.id ? String(row.id) : "",
      },
    });
  };

  const toggleParish = (parishId: string | number) => {
    const id = Number(parishId);
    setSelectedParishIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  };

  const visibleParishes = categoryParishes.filter((row) => {
    const key = parishFilter.trim().toLowerCase();
    if (!key) return true;
    return [row.parish, row.city, row.uniqueId, row.deaneryName]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(key));
  });

  const columns = [
    ...(isAdmin
      ? [
          {
            id: "action",
            name: "Action",
            cell: (row) => (
              <MasterRowActions
                onEdit={() => startEdit(row)}
                onDelete={() => setDeleteTarget(row)}
              />
            ),
          },
        ]
      : []),
    {
      id: "name",
      name: "Deanery Name",
      selector: (row) => row.deaneryName,
      sortable: true,
    },
    {
      id: "code",
      name: "Deanery Code",
      selector: (row) => row.deaneryCode,
      sortable: true,
    },
    {
      id: "category",
      name: "Diocese / Category",
      selector: (row) => row.parishCategoryName,
      sortable: true,
    },
    {
      id: "parishes",
      name: "Parishes",
      selector: (row) => row.parishCount || 0,
      sortable: true,
      cell: (row) => (
        <a
          onClick={(event) => {
            event.stopPropagation();
            openParishes(row);
          }}
          style={{
            cursor: "pointer",
            textDecoration: "underline",
            color: "blue",
          }}
        >
          {row.parishCount || 0}
        </a>
      ),
    },
    {
      id: "created",
      name: "Created At",
      selector: (row) => row.createdAt,
      sortable: true,
      cell: (row) => (
        <span>
          {row.createdAt
            ? Helpers.convertUtcToTimezone(row.createdAt, "MM-DD-YYYY")
            : ""}
        </span>
      ),
    },
  ];

  const categoryOptions = categories.map((category) => ({
    value: category.id,
    label: category.name,
  }));

  const assignmentItems = visibleParishes.map((parish) => {
    const assignedElsewhere =
      parish.deaneryId && Number(parish.deaneryId) !== Number(editingId);
    return {
      id: parish.id,
      checked: selectedParishIds.includes(Number(parish.id)),
      label: (
        <>
          {parish.parish}
          {parish.city ? ` — ${parish.city}` : ""}
          {assignedElsewhere ? ` (currently ${parish.deaneryName})` : ""}
        </>
      ),
    };
  });

  return (
    <MasterPageShell>
      <MasterSectionCard
        title={isEditing ? "Edit Deanery" : "Add Deanery"}
        subtitle={
          isEditing
            ? "Check the parishes for this deanery. Update saves them and returns to the list."
            : "Choose a diocese / category to check its parishes. Add saves the deanery and those parishes, then returns to the list."
        }
        icon={<Landmark size={20} color="#285d8f" />}
        bgcolor="#e8f3fc"
      >
        <DeaneryForm
          isEditing={isEditing}
          validated={validated}
          values={data}
          categories={categoryOptions}
          onChange={handleChanges}
          onSubmit={handleSubmit}
          onCancel={clearForm}
          saving={saving}
          parishItems={assignmentItems}
          parishFilter={parishFilter}
          onParishFilter={setParishFilter}
          onToggleParish={toggleParish}
        />
      </MasterSectionCard>

      <MasterSectionCard
        title="Deaneries"
        subtitle="Deanery records for each diocese / category."
        icon={<Landmark size={20} color="#285d8f" />}
        total={list?.length}
        bgcolor="#f3f7fb"
        toolbar={
          <MasterListToolbar
            selectValue={categoryFilter}
            selectPlaceholder="All dioceses / categories"
            selectOptions={categoryOptions}
            onSelectChange={onCategoryFilter}
            searchValue={searchKey}
            onSearch={onSearch}
            onClear={
              categoryFilter || searchKey ? () => onCategoryFilter("") : undefined
            }
            actions={
              <MasterToolbarButton
                icon={<Tags size={16} />}
                onClick={() => navigate("/parishCategories")}
              >
                Parish Categories
              </MasterToolbarButton>
            }
          />
        }
        bodyPadding={false}
      >
        <div className={styles.tableWrap}>
          <ResponsiveDataList
            loading={loading}
            data={list}
            columns={columns}
            pointerOnHover
            highlightOnHover
            isServerSide={false}
            pagination
            countPerPage={20}
            cardGridColumns={{ xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
            renderCard={(row) => (
              <DeaneryCard
                row={row}
                isAdmin={isAdmin}
                onEdit={startEdit}
                onDelete={setDeleteTarget}
                onOpenParishes={openParishes}
              />
            )}
          />
        </div>
      </MasterSectionCard>

      <DeleteModel
        show={Boolean(deleteTarget)}
        content={
          <MasterDetailBlock
            title="Deanery Detail"
            label="Deanery"
            value={deleteTarget?.deaneryName}
          />
        }
        handleClose={() => setDeleteTarget(null)}
        DeleteData={deleteDeanery}
      />
    </MasterPageShell>
  );
};

export default Deaneries;
